/**
 * Where each node of a Brain knowledge graph goes: a ring, grouped by type.
 *
 * The graph used to be one ring in database order: ~70 entities ~23 px apart with
 * ~110 px labels, so they overlapped by construction. Here the ring's radius is
 * computed FROM the number of nodes so every node gets a slot of at least
 * ARC_SPACING along it, every type owns one contiguous sector (so the ring reads
 * as groups), and labels point outward along the radius — a ring needs only as
 * much room as its circles, not as much as horizontal text would.
 *
 * Documents sit in the middle. Pure (no React, no ReactFlow): the property that
 * matters — nothing overlaps — is unit tested.
 */

export interface LayoutNode {
  id: string;
  label: string;
  name: string;
}

export interface LayoutEdge {
  source: string;
  target: string;
}

export interface PlacedNode {
  id: string;
  label: string;
  name: string;
  /** Centre of the circle. */
  cx: number;
  cy: number;
  /** Diameter of the circle. */
  size: number;
  /** `ring`: an entity on the ring, label pointing outward. `center`: a document, label underneath. */
  kind: 'ring' | 'center';
  /** Ring nodes: the outward direction in CSS degrees (0 = right, clockwise). */
  angleDeg: number;
  /** Ring nodes on the left half: the label is turned 180° so its text is never upside down. */
  flip: boolean;
}

export interface PlacedGroup {
  label: string;
  count: number;
  /** Angles in radians, 0 at the top and increasing clockwise. */
  startAngle: number;
  endAngle: number;
  midAngle: number;
  /** Where the group's title sits, relative to the centre of the ring. */
  titleX: number;
  titleY: number;
}

export interface GraphLayout {
  nodes: PlacedNode[];
  groups: PlacedGroup[];
  /** Radius of the ring of entities (0 when there are none). */
  radius: number;
  /** Half the side of the square that holds everything, titles included. */
  extent: number;
}

/** Width reserved for a document's label (it sits under its circle). */
export const NODE_WIDTH = 120;
export const DOCUMENT_SIZE = 56;
export const RING_NODE_SIZE = 30;
/** Space along the ring for one entity: its circle plus a gap. */
export const ARC_SPACING = 36;
/** Empty slots between two type sectors. */
export const GAP_SLOTS = 2;
export const RING_MIN_RADIUS = 220;
/** How far a label can reach beyond its circle (it is cut with "…" past this). */
export const LABEL_REACH = 150;
export const TITLE_OFFSET = 30;

/** Rough rendered width of a ring label (11px text), capped at what it may reach. */
export function estimateLabelWidth(name: string): number {
  return Math.min(LABEL_REACH, name.length * 6.2 + 4);
}

const DOCUMENT_SPACING = 130;
const DOCUMENT_MIN_RING = 90;
/** Free space between the documents in the middle and the ring. */
const INNER_CLEARANCE = 190;

/** Documents are laid out apart; entity types in the order people expect to read them. */
const TYPE_ORDER = ['Person', 'Organization', 'Process', 'Technology', 'Concept', 'Topic'];

function typeRank(label: string): number {
  const index = TYPE_ORDER.indexOf(label);
  return index === -1 ? TYPE_ORDER.length : index;
}

export function layoutGraph(nodes: LayoutNode[], edges: LayoutEdge[]): GraphLayout {
  if (nodes.length === 0) return { nodes: [], groups: [], radius: 0, extent: 0 };

  const degree = new Map<string, number>();
  for (const edge of edges) {
    degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1);
    degree.set(edge.target, (degree.get(edge.target) ?? 0) + 1);
  }
  // The most connected first: they are the ones people look for.
  const byConnections = (a: LayoutNode, b: LayoutNode): number =>
    (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0) ||
    a.name.localeCompare(b.name) ||
    a.id.localeCompare(b.id);

  const documents = nodes.filter((n) => n.label === 'Document').sort(byConnections);
  const byLabel = new Map<string, LayoutNode[]>();
  for (const node of nodes) {
    if (node.label === 'Document') continue;
    const list = byLabel.get(node.label);
    if (list) list.push(node);
    else byLabel.set(node.label, [node]);
  }
  const labels = [...byLabel.keys()].sort((a, b) => typeRank(a) - typeRank(b) || a.localeCompare(b));

  const placed: PlacedNode[] = [];

  // ── Documents in the middle ────────────────────────────────────────────────
  const documentRing = documents.length > 1
    ? Math.max(DOCUMENT_MIN_RING, (documents.length * DOCUMENT_SPACING) / (2 * Math.PI))
    : 0;
  documents.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / Math.max(1, documents.length) - Math.PI / 2;
    placed.push({
      id: node.id, label: node.label, name: node.name,
      // `+ 0` turns a negative zero (a radius of 0 times a negative sine) into a plain 0.
      cx: documentRing * Math.cos(angle) + 0,
      cy: documentRing * Math.sin(angle) + 0,
      size: DOCUMENT_SIZE, kind: 'center', angleDeg: 0, flip: false,
    });
  });

  // ── Entities on the ring, one sector per type ──────────────────────────────
  const slots = labels.reduce((total, label) => total + (byLabel.get(label)?.length ?? 0), 0) + GAP_SLOTS * labels.length;
  const radius = labels.length === 0
    ? 0
    : Math.max(
        RING_MIN_RADIUS,
        (slots * ARC_SPACING) / (2 * Math.PI),
        documentRing + INNER_CLEARANCE,
      );
  const step = slots > 0 ? (2 * Math.PI) / slots : 0;

  const groups: PlacedGroup[] = [];
  let cursor = 0;
  for (const label of labels) {
    const members = [...(byLabel.get(label) ?? [])].sort(byConnections);
    const firstSlot = cursor; // fixed for this sector: the cursor moves on below
    const startAngle = firstSlot * step;
    const endAngle = (firstSlot + members.length) * step;
    const midAngle = (startAngle + endAngle) / 2;
    // Just beyond the longest label of THIS sector, so the title sits next to its
    // arc instead of floating at the far edge of the drawing.
    const longest = members.reduce((max, member) => Math.max(max, estimateLabelWidth(member.name)), 0);
    const titleRadius = radius + RING_NODE_SIZE / 2 + 8 + longest + TITLE_OFFSET;
    groups.push({
      label, count: members.length, startAngle, endAngle, midAngle,
      titleX: titleRadius * Math.sin(midAngle),
      titleY: -titleRadius * Math.cos(midAngle),
    });

    members.forEach((node, index) => {
      const angle = (firstSlot + index + 0.5) * step;
      const cx = radius * Math.sin(angle);
      const cy = -radius * Math.cos(angle);
      const angleDeg = (Math.atan2(cy, cx) * 180) / Math.PI;
      placed.push({
        id: node.id, label: node.label, name: node.name,
        cx, cy, size: RING_NODE_SIZE, kind: 'ring',
        angleDeg, flip: cx < 0,
      });
    });
    cursor += members.length + GAP_SLOTS;
  }

  const outer = labels.length === 0
    ? documentRing + DOCUMENT_SIZE + 60
    : Math.max(
        radius + RING_NODE_SIZE / 2 + 8 + LABEL_REACH, // the longest label any node may have
        ...groups.map((g) => Math.hypot(g.titleX, g.titleY) + 70), // and every title, with its pill
      ) + 20;
  return { nodes: placed, groups, radius, extent: outer };
}

/**
 * A short fingerprint of a graph's contents. A canvas that keeps its own node
 * positions must be rebuilt when the data behind it changes (the graph is
 * refreshed after a background indexing finishes); this is the key for that.
 */
export function graphSignature(nodes: LayoutNode[], edges: LayoutEdge[]): string {
  let hash = 5381;
  const feed = (text: string): void => {
    for (let i = 0; i < text.length; i += 1) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
  };
  for (const node of nodes) feed(`${node.id}|${node.label}|${node.name};`);
  for (const edge of edges) feed(`${edge.source}>${edge.target};`);
  return `${nodes.length}-${edges.length}-${hash.toString(36)}`;
}
